import type { ClientRaiseTargetInvestor } from '../types/targetInvestor.types';
import { getCountryDisplayTitle } from './countryDisplay';
import { extractCountryFromLocation } from './locationUtils';

export const UNASSIGNED_LOCATION = 'Unassigned';

/** User-facing label for empty location rows in breakdowns (not used as primary tile value). */
export const UNASSIGNED_LOCATION_LABEL = 'Not set';

export interface LocationMixEntry {
  name: string;
  count: number;
}

export interface LocationMixResult {
  top: LocationMixEntry | null;
  otherCountryCount: number;
  unassignedCount: number;
  breakdown: LocationMixEntry[];
}

export function getEffectiveTargetLocation(investor: ClientRaiseTargetInvestor): string | null {
  // Partial/local records (emulator fixtures, legacy docs) may omit overrides/masterSnapshot.
  const value = (investor.overrides?.location ?? investor.masterSnapshot?.location ?? '').trim();
  return value.length > 0 ? value : null;
}

function bucketLocation(raw: string | null): string {
  if (!raw) {
    return UNASSIGNED_LOCATION;
  }
  return extractCountryFromLocation(raw);
}

function sortLocationEntries(entries: LocationMixEntry[]): LocationMixEntry[] {
  return [...entries].sort((a, b) => {
    if (a.name === UNASSIGNED_LOCATION) return 1;
    if (b.name === UNASSIGNED_LOCATION) return -1;
    if (b.count !== a.count) return b.count - a.count;
    return a.name.localeCompare(b.name);
  });
}

export function computeLocationMix(investors: ClientRaiseTargetInvestor[]): LocationMixResult {
  const counts: Record<string, number> = {};

  for (const investor of investors) {
    const bucket = bucketLocation(getEffectiveTargetLocation(investor));
    counts[bucket] = (counts[bucket] ?? 0) + 1;
  }

  const breakdown = sortLocationEntries(
    Object.entries(counts).map(([name, count]) => ({ name, count }))
  );

  if (breakdown.length === 0) {
    return {
      top: null,
      otherCountryCount: 0,
      unassignedCount: 0,
      breakdown: [],
    };
  }

  const topEntry = breakdown.find((entry) => entry.name !== UNASSIGNED_LOCATION) ?? null;
  const top = topEntry;
  const unassignedCount = counts[UNASSIGNED_LOCATION] ?? 0;
  const otherCountryCount = breakdown.filter(
    (entry) => entry.name !== top?.name && entry.name !== UNASSIGNED_LOCATION
  ).length;

  return {
    top,
    otherCountryCount,
    unassignedCount,
    breakdown,
  };
}

export function formatLocationMixBreakdown(breakdown: LocationMixEntry[]): string {
  if (breakdown.length === 0) {
    return 'No location data set.';
  }
  return breakdown
    .map(({ name, count }) => {
      const label = name === UNASSIGNED_LOCATION ? UNASSIGNED_LOCATION_LABEL : getCountryDisplayTitle(name);
      return `${label}: ${count}`;
    })
    .join(' · ');
}
